import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  IdCard, Loader2, AlertCircle, RefreshCw, Check, X,
  CheckCircle2, XCircle, Clock, Image as ImageIcon
} from 'lucide-react';
import AdminNav from './AdminNav';
import AdminGate from './AdminGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All' },
];

/** Thumbnail that lazily fetches the admin-only document endpoint as a blob,
 *  since <img src> can't be trusted to always carry cross-site cookies. */
const DocThumb = ({ proId, docType, label }) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoke = null;
    axios
      .get(`${API}/professionals/${proId}/verification-doc/${docType}`, { withCredentials: true, responseType: 'blob' })
      .then((res) => {
        revoke = URL.createObjectURL(res.data);
        setUrl(revoke);
      })
      .catch(() => setError(true));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [proId, docType]);

  if (error) {
    return (
      <div className="w-full h-32 flex flex-col items-center justify-center bg-gray-100 rounded-lg text-gray-400 text-xs gap-1">
        <ImageIcon size={18} /> Could not load {label}
      </div>
    );
  }
  if (!url) {
    return <div className="w-full h-32 flex items-center justify-center bg-gray-100 rounded-lg"><Loader2 size={18} className="animate-spin text-gray-400" /></div>;
  }
  return <img src={url} alt={label} data-testid={`doc-thumb-${docType}-${proId}`} className="w-full h-32 object-cover rounded-lg border border-gray-200" />;
};

const AdminVerificationsInner = ({ adminEmail }) => {
  const [pros, setPros] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [error, setError] = useState('');
  const [rejectReason, setRejectReason] = useState({});

  const fetchPros = useCallback(async (status) => {
    setLoading(true);
    setError('');
    try {
      const url = `${API}/admin/verifications${status ? `?status=${status}` : ''}`;
      const res = await axios.get(url, { withCredentials: true });
      setPros(res.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load verifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPros(statusFilter); }, [fetchPros, statusFilter]);

  const handleDecision = async (pro, action) => {
    setResolving(`${pro.id}:${action}`);
    setError('');
    try {
      await axios.post(
        `${API}/admin/professionals/${pro.id}/verify`,
        { action, reason: rejectReason[pro.id] || '' },
        { withCredentials: true }
      );
      await fetchPros(statusFilter);
    } catch (e) {
      setError(e.response?.data?.detail || `Failed to ${action} verification`);
    } finally {
      setResolving(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav activeEmail={adminEmail} />
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#37b34a]/10 rounded-xl flex items-center justify-center">
              <IdCard size={18} className="text-[#37b34a]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Identity Verifications</h1>
              <p className="text-xs text-gray-500">Review ID documents &amp; selfies</p>
            </div>
          </div>
          <button
            onClick={() => fetchPros(statusFilter)}
            data-testid="verifications-refresh-btn"
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="verifications-filter-chips">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              onClick={() => setStatusFilter(f.value)}
              data-testid={`verifications-filter-${f.value || 'all'}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                statusFilter === f.value ? 'bg-[#37b34a] text-white border-[#37b34a]' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 text-sm px-4 py-2 rounded-lg flex items-center gap-2 mb-4">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {loading && pros.length === 0 ? (
          <div className="py-16 flex items-center justify-center"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
        ) : pros.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl py-12 text-center">
            <IdCard size={32} className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No {statusFilter || ''} verifications.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-verifications-list">
            {pros.map((p) => (
              <div key={p.id} className="bg-white border border-gray-200 rounded-xl p-5" data-testid={`verification-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {p.verification_status === 'pending' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200"><Clock size={10} /> Pending</span>
                      )}
                      {p.verification_status === 'approved' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold border border-green-200"><CheckCircle2 size={10} /> Approved</span>
                      )}
                      {p.verification_status === 'rejected' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold border border-red-200"><XCircle size={10} /> Rejected</span>
                      )}
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">{p.name}</h3>
                    <p className="text-xs text-gray-500">{p.phone} · {p.email}</p>
                    <p className="text-xs text-gray-500">{p.city}, {p.province} · ID: {p.id_number || '—'}</p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {p.verification_submitted_at ? new Date(p.verification_submitted_at).toLocaleString() : ''}
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  {p.has_id_document ? <DocThumb proId={p.id} docType="id_document" label="ID document" /> : <div className="w-full h-32 flex items-center justify-center bg-gray-50 rounded-lg text-xs text-gray-400">No ID document</div>}
                  {p.has_selfie ? <DocThumb proId={p.id} docType="selfie" label="Selfie" /> : <div className="w-full h-32 flex items-center justify-center bg-gray-50 rounded-lg text-xs text-gray-400">No selfie</div>}
                </div>

                {p.verification_status === 'pending' ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Optional rejection reason (shown to pro)"
                      value={rejectReason[p.id] || ''}
                      onChange={(e) => setRejectReason((prev) => ({ ...prev, [p.id]: e.target.value }))}
                      maxLength={500}
                      data-testid={`reject-reason-input-${p.id}`}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#37b34a]"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDecision(p, 'approve')}
                        disabled={!!resolving}
                        data-testid={`approve-verification-${p.id}`}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold text-white bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 transition-colors"
                      >
                        {resolving === `${p.id}:approve` ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Approve
                      </button>
                      <button
                        onClick={() => handleDecision(p, 'reject')}
                        disabled={!!resolving}
                        data-testid={`reject-verification-${p.id}`}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
                      >
                        {resolving === `${p.id}:reject` ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />} Reject
                      </button>
                    </div>
                  </div>
                ) : (
                  p.verification_rejection_reason && (
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <span className="font-semibold">Rejection reason:</span> {p.verification_rejection_reason}
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const AdminVerifications = () => (
  <AdminGate>
    {({ adminEmail }) => <AdminVerificationsInner adminEmail={adminEmail} />}
  </AdminGate>
);

export default AdminVerifications;
